import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, Car, Sparkles, Shield, MapPin, Phone, Mail, ArrowRight, Menu, X, Instagram, Wrench, Award } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lightbox component for viewing images in full quality
const Lightbox = ({ image, onClose }) => {
  if (!image) return null;
  
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <button 
        className="absolute top-6 right-6 text-white hover:text-zinc-300 z-[101]"
        onClick={onClose}
      >
        <X className="w-8 h-8" />
      </button>
      <img 
        src={image.src} 
        alt={image.alt}
        className="max-w-full max-h-[90vh] object-contain rounded-lg"
      />
      {image.caption && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/80 px-4 py-2 rounded-lg">
          <p className="text-white text-sm">{image.caption}</p>
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-zinc-800">
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
              <Car className="w-6 h-6 text-black" />
            </div>
            <span className="text-xl font-medium tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Esthétique Auto TR
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium" data-testid="nav-services">Services</a>
            <a href="#about" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium" data-testid="nav-about">À Propos</a>
            <a href="#results" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium" data-testid="nav-results">Résultats</a>
            <a href="#contact" className="text-zinc-400 hover:text-white transition-colors text-sm font-medium" data-testid="nav-contact">Contact</a>
          </nav>

          <div className="hidden md:block">
            <Link to="/booking">
              <Button 
                className="bg-white text-black hover:bg-zinc-200 font-medium px-6"
                data-testid="header-book-btn"
              >
                Réserver
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-zinc-800">
            <nav className="flex flex-col gap-4">
              <a href="#services" className="text-zinc-400 hover:text-white text-sm font-medium py-2" onClick={() => setIsMenuOpen(false)}>Services</a>
              <a href="#about" className="text-zinc-400 hover:text-white text-sm font-medium py-2" onClick={() => setIsMenuOpen(false)}>À Propos</a>
              <a href="#results" className="text-zinc-400 hover:text-white text-sm font-medium py-2" onClick={() => setIsMenuOpen(false)}>Résultats</a>
              <a href="#contact" className="text-zinc-400 hover:text-white text-sm font-medium py-2" onClick={() => setIsMenuOpen(false)}>Contact</a>
              <Link to="/booking" onClick={() => setIsMenuOpen(false)}>
                <Button className="bg-white text-black hover:bg-zinc-200 font-medium w-full mt-2">
                  Réserver
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/ry392bmu_image.png"
          alt="Luxury car"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container-custom text-center pt-20">
        <div className="fade-in">
          <span className="text-xs tracking-[0.3em] uppercase text-zinc-400 mb-6 inline-block">Detailing Véhicules de Luxe</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            L'Excellence du <br />
            <span className="text-zinc-300">Detailing d'Exception</span>
          </h1>
          <p className="text-base text-zinc-400 leading-relaxed max-w-2xl mx-auto mb-10">
            Spécialisé dans les véhicules de luxe, j'offre des services de detailing 
            d'exception pour votre véhicule. Service à domicile ou en garage disponible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button 
                className="bg-white text-black hover:bg-zinc-200 font-medium px-8 py-6 text-base"
                data-testid="hero-book-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Prendre Rendez-vous
              </Button>
            </Link>
            <a href="#services">
              <Button 
                variant="outline" 
                className="border-zinc-700 text-white hover:border-zinc-500 hover:bg-zinc-900 px-8 py-6 text-base"
                data-testid="hero-services-btn"
              >
                Découvrir mes Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-zinc-600 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, index }) => {
  const navigate = useNavigate();
  
  return (
    <div 
      className="group bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden cursor-pointer hover:border-zinc-600 transition-all duration-500"
      onClick={() => navigate(`/booking/${service.id}`)}
      data-testid={`service-card-${service.id}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="aspect-[4/3] relative overflow-hidden">
        <img 
          src={service.image_url} 
          alt={service.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {service.price_text || `${service.price} $`}
          </span>
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl sm:text-2xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {service.name}
        </h3>
        <p className="text-zinc-400 text-sm leading-relaxed mb-4 line-clamp-3">
          {service.description}
        </p>
        {service.includes && (
          <ul className="space-y-2 mb-4">
            {service.includes.slice(0, 3).map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-xs text-zinc-500">
                <div className="w-1 h-1 bg-white rounded-full"></div>
                {item}
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
          <span className="text-white text-sm font-medium group-hover:translate-x-1 transition-transform duration-300 flex items-center">
            Réserver <ArrowRight className="w-4 h-4 ml-1" />
          </span>
        </div>
      </div>
    </div>
  );
};

const ServicesSection = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <section id="services" className="py-24 bg-black" data-testid="services-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 inline-block">Mes Services</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Des Prestations d'Excellence
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-zinc-900 rounded-xl h-[500px] animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="about" className="py-24 bg-zinc-950" data-testid="about-section">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 inline-block">À Propos</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Une Passion Avant Tout
            </h2>
            <div className="space-y-4 text-zinc-400 leading-relaxed">
              <p>
                C'est avant tout une <strong className="text-white">passion des voitures</strong> qui m'a lancé dans cette aventure. 
                Chaque véhicule mérite d'être traité avec le plus grand soin et la plus haute attention aux détails.
              </p>
              <p>
                Formé par <strong className="text-white">Labautodetail</strong>, ma technique de travail est poussée 
                et perfectionnée pour offrir des résultats exceptionnels. J'utilise des produits spécialisés en detailing 
                et travaille avec <strong className="text-white">Labocosmetica</strong> pour les protections céramiques.
              </p>
              <p>
                <em className="text-zinc-300">"L'important, c'est le résultat, pas le temps."</em> 
                Je ne compte pas les heures tant que je ne suis pas pleinement satisfait du résultat.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-lg">
                <Award className="w-8 h-8 text-white mb-3" />
                <h4 className="text-white font-medium mb-1">Formation Certifiée</h4>
                <p className="text-xs text-zinc-500">Labautodetail</p>
              </div>
              <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-lg">
                <Sparkles className="w-8 h-8 text-white mb-3" />
                <h4 className="text-white font-medium mb-1">Produits Premium</h4>
                <p className="text-xs text-zinc-500">Les meilleurs produits sélectionnés pour une meilleure qualité</p>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="aspect-square rounded-2xl overflow-hidden border border-zinc-800">
              <img 
                src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/iy8mmx29_A77907A5-FE9B-4652-A86E-CA539B57F4AE_1_105_c.jpeg"
                alt="Detail work"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ResultsSection = () => {
  const [lightboxImage, setLightboxImage] = useState(null);

  const openLightbox = (src, alt, caption) => {
    setLightboxImage({ src, alt, caption });
  };

  const handleImageClick = (e, src, alt, caption) => {
    e.preventDefault();
    openLightbox(src, alt, caption);
  };

  return (
    <section id="results" className="py-24 bg-black" data-testid="results-section">
      <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 inline-block">Résultats</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            L'Important, C'est le Résultat
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Pas le temps. Voici des exemples concrets de transformations que je réalise pour mes clients.
            <span className="block text-sm text-zinc-500 mt-2">Cliquez sur les images pour les agrandir</span>
          </p>
        </div>

        {/* Video Section */}
        <div className="mb-16">
          <h3 className="text-xl font-medium text-white mb-2 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Application d'une Protection Céramique
          </h3>
          <p className="text-sm text-zinc-500 text-center mb-6 max-w-2xl mx-auto">
            Découvrez mon travail en vidéo
          </p>
          <div className="flex justify-center">
            <a 
              href="https://www.instagram.com/reel/DS3poIJjPSQ/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group relative rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all max-w-md"
            >
              <div className="aspect-[9/16] bg-zinc-900 flex items-center justify-center relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-pink-500/20 to-orange-400/20"></div>
                <div className="text-center z-10 p-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <polygon points="5,3 19,12 5,21" />
                    </svg>
                  </div>
                  <Instagram className="w-8 h-8 text-white mx-auto mb-3" />
                  <p className="text-white font-medium mb-1">Voir la vidéo sur Instagram</p>
                  <p className="text-zinc-400 text-sm">@Emerick_levasseur</p>
                </div>
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-lg font-medium">▶ Regarder</span>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Protection Céramique Process */}
        <div className="mb-16">
          <h3 className="text-xl font-medium text-white mb-2 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Protection Céramique - Audi TTS
          </h3>
          <p className="text-sm text-zinc-500 text-center mb-6 max-w-2xl mx-auto">
            Découvrez les étapes d'une application de protection céramique professionnelle avec les produits Labocosmetica.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            <div 
              className="relative group cursor-pointer select-none"
              onClick={(e) => handleImageClick(e, 
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/5h2wh5zm_A0C7D7D5-1144-4D27-A8DF-F96073566D4D_1_102_o.jpeg",
                "Masquage",
                "Étape 1 - Masquage pour protéger les surfaces"
              )}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div className="aspect-square rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/5h2wh5zm_A0C7D7D5-1144-4D27-A8DF-F96073566D4D_1_102_o.jpeg"
                  alt="Étape 1 - Masquage"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-3 left-3 bg-black/80 text-white text-xs font-bold px-2 py-1 rounded">
                1. Masquage
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
            
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/wcn63b72_6025AA86-286B-4F75-9E55-F09CE877D775_1_106_c.jpeg",
                "Application céramique",
                "Étape 2 - Application de la protection céramique"
              )}
            >
              <div className="aspect-square rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/wcn63b72_6025AA86-286B-4F75-9E55-F09CE877D775_1_106_c.jpeg"
                  alt="Étape 2 - Application"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-3 left-3 bg-black/80 text-white text-xs font-bold px-2 py-1 rounded">
                2. Application
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
            
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/k2xvgtm1_5530F5F7-ED79-4949-8BAB-66719A8B7B6A_1_106_c.jpeg",
                "Retirer le surplus",
                "Étape 3 - Retirer le surplus avec microfibre"
              )}
            >
              <div className="aspect-square rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/k2xvgtm1_5530F5F7-ED79-4949-8BAB-66719A8B7B6A_1_106_c.jpeg"
                  alt="Étape 3 - Retirer le surplus"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-3 left-3 bg-black/80 text-white text-xs font-bold px-2 py-1 rounded">
                3. Retirer le surplus
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
            
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/1wavqm6q_516F50EF-C5DA-4D70-A491-B3F75FF37098_1_102_o.jpeg",
                "Résultat céramique",
                "Étape 4 - Résultat final"
              )}
            >
              <div className="aspect-square rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/1wavqm6q_516F50EF-C5DA-4D70-A491-B3F75FF37098_1_102_o.jpeg"
                  alt="Étape 4 - Résultat"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute bottom-3 left-3 bg-black/80 text-white text-xs font-bold px-2 py-1 rounded">
                4. Résultat
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
          </div>
        </div>

        {/* Polissage Before/After */}
        <div className="mb-16">
          <h3 className="text-xl font-medium text-white mb-2 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Polissage
          </h3>
          <p className="text-sm text-zinc-500 text-center mb-6 max-w-2xl mx-auto">
            Un de mes plus gros polissages. Il est normal qu'avec le temps, les égratignures s'accumulent et cela peut paraître irréparable. Mais avec les bonnes connaissances et les bons outils, tout peut se régler !
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/8uukwhb7_A5D945F9-6359-45F6-B7B6-59953C7E8F32_1_102_o.jpeg",
                "Polissage avant",
                "Avant - Égratignures accumulées"
              )}
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/8uukwhb7_A5D945F9-6359-45F6-B7B6-59953C7E8F32_1_102_o.jpeg"
                  alt="Polissage - Avant"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Avant
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
            
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/b6vdja72_C4DA9F38-0536-4879-AF78-6B0F25EADF70_1_102_o.jpeg",
                "Polissage après",
                "Après - Surface lisse et brillante"
              )}
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/b6vdja72_C4DA9F38-0536-4879-AF78-6B0F25EADF70_1_102_o.jpeg"
                  alt="Polissage - Après"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute top-4 left-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Après
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
          </div>
        </div>

        {/* Mercedes C63S Before/After */}
        <div className="mb-16">
          <h3 className="text-xl font-medium text-white mb-2 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Mercedes C63S
          </h3>
          <p className="text-sm text-zinc-500 text-center mb-6 max-w-2xl mx-auto">
            Saviez-vous que les freins des Mercedes sont parmi ceux qui produisent le plus de poussière de frein ? Les résultats avant/après sont toujours impressionnants !
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/7gno954a_0F0C93D9-66B0-4FB1-93D2-62937A886548_1_102_o.jpeg",
                "Mercedes C63S avant",
                "Avant - Roue couverte de poussière de frein"
              )}
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/7gno954a_0F0C93D9-66B0-4FB1-93D2-62937A886548_1_102_o.jpeg"
                  alt="Mercedes C63S - Avant"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Avant
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
            
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/jsg3cnz3_ECF83107-259B-4EAB-9B28-13D985A8070A_1_102_o.jpeg",
                "Mercedes C63S après",
                "Après - Roue impeccable"
              )}
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/jsg3cnz3_ECF83107-259B-4EAB-9B28-13D985A8070A_1_102_o.jpeg"
                  alt="Mercedes C63S - Après"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute top-4 left-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Après
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
          </div>
        </div>

        {/* Audi R8 Showcase */}
        <div className="mb-16">
          <h3 className="text-xl font-medium text-white mb-6 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Audi R8
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/42cl2xcb_3052F465-23A1-40D8-983A-A7367242045B_1_102_o.jpeg",
                "Audi R8 avant",
                "Avant"
              )}
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/42cl2xcb_3052F465-23A1-40D8-983A-A7367242045B_1_102_o.jpeg"
                  alt="Audi R8 - Avant"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Avant
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
            
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/dke0ymtk_A8574C96-3B97-41C9-9582-1F150015DDD7_1_102_o.jpeg",
                "Audi R8 après",
                "Après"
              )}
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/dke0ymtk_A8574C96-3B97-41C9-9582-1F150015DDD7_1_102_o.jpeg"
                  alt="Audi R8 - Après"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute top-4 left-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Après
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
          </div>
        </div>

        {/* Interior Before/After */}
        <div className="mb-12">
          <h3 className="text-xl font-medium text-white mb-6 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Remise à Neuf - Intérieur
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/mnbkdemq_72A58615-7566-4882-AB6B-0DCC5E2CE6DA_1_102_o.jpeg",
                "Intérieur avant",
                "Avant - État initial"
              )}
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/mnbkdemq_72A58615-7566-4882-AB6B-0DCC5E2CE6DA_1_102_o.jpeg"
                  alt="Avant - État initial"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Avant
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
            
            <div 
              className="relative group cursor-pointer"
              onClick={() => openLightbox(
                "https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/apbx9v02_3D939EF1-8DA5-4F6D-AFC1-9ADDE91E71CE_1_102_o.jpeg",
                "Intérieur après",
                "Après - Résultat final"
              )}
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/apbx9v02_3D939EF1-8DA5-4F6D-AFC1-9ADDE91E71CE_1_102_o.jpeg"
                  alt="Après - Résultat final"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute top-4 left-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                Après
              </div>
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <span className="text-white text-sm font-medium">Agrandir</span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-zinc-500 text-sm italic mb-8">
            "Tant que je ne suis pas satisfait, je continue. Les heures ne comptent pas."
          </p>
          <Link to="/booking">
            <Button 
              className="bg-white text-black hover:bg-zinc-200 font-medium px-8 py-4"
              data-testid="results-book-btn"
            >
              Réserver Maintenant
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Qualité Premium",
      description: "Produits italiens haut de gamme et Labocosmetica pour des résultats exceptionnels."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Protection Durable",
      description: "Protections céramiques professionnelles pour une durabilité maximale."
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Service Flexible",
      description: "À domicile ou en garage avec échangeur d'air pour les protections céramiques."
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      title: "Travail Minutieux",
      description: "Je peux retirer les roues pour un nettoyage optimal. Le résultat prime sur le temps."
    }
  ];

  return (
    <section className="py-24 bg-zinc-950" data-testid="features-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 inline-block">Pourquoi Me Choisir</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            L'Excellence à Votre Service
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="p-6 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-all duration-300"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center text-white mb-4">
                {feature.icon}
              </div>
              <h3 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                {feature.title}
              </h3>
              <p className="text-sm text-zinc-500">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  return (
    <section className="py-24 bg-black relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0 opacity-10">
        <img 
          src="https://customer-assets.emergentagent.com/job_detail-booking-3/artifacts/ry392bmu_image.png"
          alt="Background"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black"></div>
      
      <div className="container-custom relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 inline-block">Prêt à Sublimer Votre Véhicule ?</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Réservez Votre Rendez-vous <br />Dès Maintenant
          </h2>
          <p className="text-zinc-400 mb-4 max-w-xl mx-auto">
            Offrez à votre véhicule le traitement qu'il mérite. 
            Disponible les fins de semaine et en semaine à partir de 16h.
          </p>
          <p className="text-zinc-500 text-sm mb-8">
            Service à domicile ou en garage avec échangeur d'air disponible.
          </p>
          <Link to="/booking">
            <Button 
              className="bg-white text-black hover:bg-zinc-200 font-medium px-10 py-6 text-base"
              data-testid="cta-book-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Prendre Rendez-vous
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer id="contact" className="py-16 bg-zinc-950 border-t border-zinc-900" data-testid="footer">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                <Car className="w-6 h-6 text-black" />
              </div>
              <span className="text-xl font-medium text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Esthétique Auto TR
              </span>
            </div>
            <p className="text-sm text-zinc-500 leading-relaxed mb-4">
              Spécialiste du detailing automobile de luxe à Trois-Rivières. 
              Formé par Labo Auto Detail, je sublime votre véhicule avec passion et expertise.
            </p>
            <a 
              href="https://instagram.com/Emerick_levasseur" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white hover:text-zinc-300 transition-colors"
            >
              <Instagram className="w-5 h-5" />
              <span className="text-sm">@Emerick_levasseur</span>
            </a>
          </div>

          <div>
            <h4 className="text-sm font-medium text-white mb-4 uppercase tracking-wider">Services</h4>
            <ul className="space-y-3">
              <li><Link to="/booking/service-1" className="text-sm text-zinc-500 hover:text-white transition-colors">Remise à Neuf - 350 $</Link></li>
              <li><Link to="/booking/service-2" className="text-sm text-zinc-500 hover:text-white transition-colors">Polissage - 600 $</Link></li>
              <li><Link to="/booking/service-3" className="text-sm text-zinc-500 hover:text-white transition-colors">Protection Céramique - 1000 $+</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-medium text-white mb-4 uppercase tracking-wider">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-zinc-500">
                <Phone className="w-4 h-4 text-white" />
                <a href="tel:8192667871" className="hover:text-white transition-colors">
                  819 266-7871
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-zinc-500">
                <Mail className="w-4 h-4 text-white" />
                <a href="mailto:esthetiqueautotr@gmail.com" className="hover:text-white transition-colors">
                  esthetiqueautotr@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-zinc-500">
                <Mail className="w-4 h-4 text-white" />
                <a href="mailto:2popojo@gmail.com" className="hover:text-white transition-colors">
                  2popojo@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm text-zinc-500">
                <MapPin className="w-4 h-4 text-white mt-0.5" />
                <span>Trois-Rivières, QC<br/>Service à domicile ou en garage</span>
              </li>
            </ul>
            <div className="mt-4 p-3 bg-zinc-900 rounded-lg border border-zinc-800">
              <p className="text-xs text-zinc-400">
                <strong className="text-white">Horaires :</strong><br />
                Fins de semaine : Toute la journée<br />
                Semaine : À partir de 16h
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-zinc-900 text-center">
          <p className="text-xs text-zinc-600">
            © {new Date().getFullYear()} Esthétique Auto TR - Trois-Rivières. Tous droits réservés. | Formé par Labautodetail
          </p>
        </div>
      </div>
    </footer>
  );
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-black" data-testid="home-page">
      <Header />
      <HeroSection />
      <ServicesSection />
      <AboutSection />
      <ResultsSection />
      <FeaturesSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default HomePage;
